import moment from 'moment';
import { ownerTypeAgent, ownerTypeHousehold } from 'Consts/Strings';

export const EmailCredentialsCompleted = 'EmailCredentialsCompleted';

class ActionItem {
  static get TYPE() {
    return 'actionItem';
  }

  static get WORKFLOW_ID_COLLECT_CREDIT_CARD_INFORMATION_COMPLETED() {
    return 'CollectCreditCardInformationCompleted';
  }

  static get WORKFLOW_ID_RECOMMEND_PRELIMINARY_SERVICE_LEVEL_COMPLETED() {
    return 'RecommendPreliminaryServiceLevelCompleted';
  }

  static get WORKFLOW_ID_SERVICE_LEVEL_SELECTED() {
    return 'ServiceLevelSelected';
  }

  static get WORKFLOW_ID_FINALIZE_ONLINE_SERVICE_LEVEL_COMPLETED() {
    return 'FinalizeOnlineServiceLevelCompleted';
  }

  static get WORKFLOW_GROUP_KEY_TRANSITION() {
    return 'transition';
  }

  static get WORKFLOW_GROUP_KEY_ONBOARDING() {
    return 'on-boarding';
  }

  static get WORKFLOW_GROUP_KEY_PLANNING() {
    return 'planning';
  }

  static get WORKFLOW_GROUP_KEY_SERVICE() {
    return 'service';
  }

  static get STATUS_TODO() {
    return 'todo';
  }

  static get STATUS_IN_PROGRESS() {
    return 'in-progress';
  }

  static get STATUS_PENDING() {
    return 'pending';
  }

  static get STATUS_REVIEW() {
    return 'review';
  }

  static get STATUS_COMPLETED() {
    return 'completed';
  }

  static get STATUS_NOT_APPLICABLE() {
    return 'not-applicable';
  }

  static get PRIORITY_LOW() {
    return 'low';
  }

  static get PRIORITY_MED() {
    return 'medium';
  }

  static get PRIORITY_HIGH() {
    return 'high';
  }

  static isMilestone(actionItem) {
    return !actionItem.id;
  }

  static get FILTER_TEXT() {
    return 'filterText';
  }

  static get FILTER_ASSIGNED_TO_ME() {
    return 'filterAssignedToMe';
  }

  static get FILTER_ASSIGNED_TO_CLIENTS() {
    return 'filterAssignedToClients';
  }

  static get FILTER_DUE_DATE_START() {
    return 'filterDueDateStart';
  }

  static get FILTER_DUE_DATE_END() {
    return 'filterDueDateEnd';
  }

  static get FILTER_PRIORITY_LOW() {
    return 'filterPriorityLow';
  }

  static get FILTER_PRIORITY_MED() {
    return 'filterPriorityMed';
  }

  static get FILTER_PRIORITY_HIGH() {
    return 'filterPriorityHigh';
  }

  // sortTOPS sorts actionItems in the order dictated by the TOPS workflow.
  // This is necessary because there is no explicit ordering of workflow items
  // on the backend and we are not guaranteed to receive the correct ordering
  // from the API.
  static sortTOPS(actionItems) {
    const order = [
      'TransitionCallCompleted',
      'ScheduleIntroductoryCallCompleted',
      'EmailWelcomePacketComplete',
      'IntroductoryCallAgendaCompleted',
      'FactFindingCallCompleted',
      'IntroductoryCallCompleted',
      'ScheduleDiscoveryCallCompleted',
      'DiscoveryCallCompleted',
      'SignedPlanningAgreementCompleted',
      'LetsGoMeetingCompleted',
      'SchedulePlanningSessionCompleted',
      'PlanningSessionCompleted',
      'ScheduleServiceSessionComplete',
    ];
    const sorted = [];
    for (let i = 0; i < order.length; i++) {
      sorted.push(
        actionItems.find(item => item.attributes.workflowID === order[i]),
      );
    }
    return sorted.filter(item => item !== undefined);
  }

  static planningCompleted(actionItems) {
    return actionItems
      .filter(
        item => item.attributes.workflowGroupKey
          === ActionItem.WORKFLOW_GROUP_KEY_PLANNING,
      )
      .every(item => item.attributes.status === ActionItem.STATUS_COMPLETED);
  }

  static serviceCompleted(actionItems) {
    return actionItems
      .filter(
        item => item.attributes.workflowGroupKey
          === ActionItem.WORKFLOW_GROUP_KEY_SERVICE,
      )
      .every(item => item.attributes.status === ActionItem.STATUS_COMPLETED);
  }

  static overdue(actionItems) {
    return actionItems.filter(ai => this.aiIsOverdue(ai));
  }

  static aiIsOverdue(ai) {
    const today = moment().startOf('day');

    return (
      ai.status !== ActionItem.STATUS_COMPLETED
      && ai.dueDate
      && ai.dueDate.isBefore(today)
    );
  }

  static dueThisWeek(actionItems) {
    // NOTE: Facet weeks are Monday through Sunday
    const startOfWeek = moment()
      .startOf('week')
      .add(1, 'day');
    const endOfWeek = moment()
      .endOf('week')
      .add(1, 'day');
    return actionItems.filter(
      ai => ai.dueDate
        && ai.dueDate.isSameOrAfter(startOfWeek)
        && ai.dueDate.isSameOrBefore(endOfWeek),
    );
  }

  static ownedByPlanner(actionItems) {
    return actionItems.filter(ai => ai.ownerType === ownerTypeAgent);
  }

  static ownedByClient(actionItems) {
    return actionItems.filter(ai => ai.ownerType === ownerTypeHousehold);
  }

  static isPriorityLow(actionItem) {
    return actionItem.priority === ActionItem.PRIORITY_LOW;
  }

  static isPriorityMed(actionItem) {
    return actionItem.priority === ActionItem.PRIORITY_MED;
  }

  static isPriorityHigh(actionItem) {
    return actionItem.priority === ActionItem.PRIORITY_HIGH;
  }

  static notCompleted(actionItems) {
    return actionItems.filter(ai => ai.status !== ActionItem.STATUS_COMPLETED);
  }

  static wasCompletedMoreThanXDaysAgo(actionItem, numDays) {
    if (actionItem.status !== ActionItem.STATUS_COMPLETED) {
      return false;
    }
    // updatedAt to handle exceptions where completedDate is not set; this should not happen
    return moment(actionItem.completedDate || actionItem.updatedAt)
      .add(numDays, 'days')
      .isBefore(moment());
  }

  static fromMilestone(milestone) {
    const actionItem = new ActionItem();
    actionItem.workflowID = milestone.eventID;
    actionItem.status = '';
    actionItem.workflowGroupKey = milestone.phase;
    return actionItem;
  }

  static fromAPIResponse(res) {
    if (res.type !== ActionItem.TYPE) {
      return new ActionItem();
    }
    let {
      dueDate = null,
      completedDate = null,
      statusUpdatedAt = null,
    } = res.attributes;
    const { ownerType } = res.attributes;
    if (dueDate) {
      dueDate = moment(dueDate);
    }
    if (completedDate) {
      completedDate = moment(completedDate);
    }
    if (statusUpdatedAt) {
      statusUpdatedAt = moment(statusUpdatedAt);
    }
    return Object.assign(new ActionItem(), res.attributes, {
      id: res.id,
      dueDate,
      completedDate,
      statusUpdatedAt,
      isOwnedByClient: ownerType === ownerTypeHousehold,
    });
  }

  static toAPIRequest(actionItem) {
    return {
      id: actionItem.id,
      type: ActionItem.TYPE,
      attributes: { ...actionItem, id: +actionItem.id },
    };
  }

  constructor(
    id,
    owner,
    ownerId,
    name,
    description,
    dueDate,
    priority,
    status,
    workflowID,
    workflowGroupKey,
    isOwnedByClient,
    ownerType,
    milestone,
    createdByName,
    timeToComplete,
  ) {
    this.id = id;
    this.owner = owner;
    this.ownerId = ownerId;
    this.name = name;
    this.description = description;
    this.dueDate = dueDate;
    this.priority = priority;
    this.status = status;
    this.workflowID = workflowID;
    this.workflowGroupKey = workflowGroupKey;
    this.isOwnedByClient = isOwnedByClient;
    this.ownerType = ownerType;
    this.milestone = milestone;
    this.createdByName = createdByName;
    this.timeToComplete = timeToComplete;
  }
}

export default ActionItem;
